import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { ArrowDown, ArrowDownUp, ArrowUp, Bookmark, ChevronDown, Columns3, Download, Group, Rows3, Upload, type LucideIcon } from 'lucide-react';
import { Superfiltro } from '@/components/datos/Superfiltro';
import { SelectorDeVista } from '@/components/ui/SelectorDeVista';
import { ORDENES, facetasDeClientes, type Filtro } from '@/lib/cartera';
import { cn } from '@/lib/cn';
import { PREF_CARTERA_DE_FABRICA, vistaAplicada, type PrefCartera } from '@/lib/pref-cartera';
import { AGRUPAR, COLUMNAS_DE_ENTRADA, alternarColumna, columnasALaVista, moverColumna } from '@/lib/tablero-clientes';
import { PanelDeVistas } from './MisVistas';
import { VISTAS } from './vistas-de-clientes';

/**
 * LA BARRA DE HERRAMIENTAS DE «CLIENTES» (tanda 6; la del tablero de Monday que mandó Kervens):
 * una sola para las cuatro vistas. Arriba, cómo verlos y, a la derecha, «Mis vistas», importar y
 * exportar; debajo, buscar y los botones que despliegan un panel: filtro, orden y, solo en la
 * tabla, agrupar, columnas y densidad. Lo que no aplica a la vista actual no se dibuja.
 *
 * No guarda nada: todo lo que se toca es un cambio a la preferencia `cartera` (`onPref`), y por
 * eso cualquier combinación se puede guardar como una vista. La búsqueda va aparte: no se guarda.
 *
 * Los paneles: en escritorio, una caja fija a la pantalla debajo de su botón (dentro de la
 * página la recortaría el desplazamiento); en el teléfono, una hoja que sube desde abajo. Se
 * cierran con Escape, tocando fuera o con «Listo».
 */
export type PanelDeBarra = 'vistas' | 'orden' | 'agrupar' | 'columnas' | 'densidad';

export interface BarraDeClientesProps {
  pref: PrefCartera;
  onPref: (cambio: (antes: PrefCartera) => PrefCartera) => void;
  busqueda: string;
  onBuscar: (texto: string) => void;
  /** Las etapas del negocio, para filtrar por una. */
  etapas: readonly string[];
  /** La etapa por la que se filtra de verdad: `pref.etapa`, o `''` si el negocio ya no tiene esa etapa. */
  etapa: string;
  /** Cuántos clientes caen en cada filtro, con la búsqueda y la etapa ya puestas. */
  cuentas: Partial<Record<Filtro, number>>;
  /** T14: las fuentes de la lista con cuántos trae cada una (`fuentesDe`), para filtrar por ellas. */
  fuentes?: ReadonlyArray<{ fuente: string; cuantos: number }>;
  /** Las columnas de la tabla como se ofrecen: delante las que se ven, en su orden (`columnasDeClientes`). */
  columnas: ReadonlyArray<{ id: string; titulo: string }>;
  /** Sin esto no hay botón (el negocio no tiene el módulo de importar). */
  onImportar?: () => void;
  onExportar?: () => void;
  /** El contador del superfiltro: «12 de 40 contactos». */
  resultados?: string;
  /** Para las pruebas: el panel que arranca abierto. */
  panelDeEntrada?: PanelDeBarra | null;
}

const DENSIDADES: ReadonlyArray<{ id: PrefCartera['densidad']; etiqueta: string }> = [
  { id: 'comoda', etiqueta: 'Cómoda' },
  { id: 'compacta', etiqueta: 'Compacta' },
];

/** Título y ancho (px, en escritorio) de cada panel. */
const PANELES: Record<PanelDeBarra, { titulo: string; ancho: number }> = {
  vistas: { titulo: 'Mis vistas', ancho: 360 },
  orden: { titulo: 'Ordenar por', ancho: 240 },
  agrupar: { titulo: 'Agrupar por', ancho: 240 },
  columnas: { titulo: 'Columnas', ancho: 320 },
  densidad: { titulo: 'Densidad', ancho: 220 },
};

const FOCO = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-600';

/**
 * Un botón de la barra. Con poco ancho se queda en el icono (el nombre sigue en `title` y
 * `aria-label`). `activo`: lo que maneja no está como viene de fábrica (hay un filtro puesto, otro
 * orden…): va en color y con un punto. Sin estado: se prueba con renderToStaticMarkup.
 */
export function BotonDeBarra({
  icono: Icono,
  etiqueta,
  nombre = etiqueta,
  ayuda,
  abierto,
  activo = false,
  conTextoDesde = 'lg',
  conFlecha = false,
  onClick,
}: {
  icono: LucideIcon;
  /** Lo que se lee en el botón. */
  etiqueta: string;
  /** Para el lector de pantalla, si hace falta decir más que la etiqueta. */
  nombre?: string;
  ayuda?: string;
  /** Si despliega un panel: abierto o cerrado. Sin esto es un botón que hace algo y ya. */
  abierto?: boolean;
  activo?: boolean;
  /** Desde qué ancho de pantalla se lee la etiqueta; por debajo, solo el icono. */
  conTextoDesde?: 'lg' | 'xl';
  conFlecha?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}) {
  const texto = conTextoDesde === 'xl' ? 'hidden xl:inline' : 'hidden lg:inline';
  return (
    <button
      type="button"
      aria-label={nombre}
      aria-expanded={abierto}
      aria-haspopup={abierto === undefined ? undefined : 'dialog'}
      data-activo={activo ? 'true' : 'false'}
      title={ayuda ?? nombre}
      onClick={onClick}
      className={cn(
        'relative inline-flex h-11 min-w-[44px] shrink-0 items-center justify-center gap-1.5 rounded-lg border px-2.5 text-sm font-medium transition-colors md:h-10 md:min-w-[40px]',
        FOCO,
        activo
          ? 'border-brand-300 bg-brand-50 text-brand-700 hover:bg-brand-100 dark:border-brand-500/60 dark:bg-brand-900/30 dark:text-brand-200 dark:hover:bg-brand-900/50'
          : 'border-surface-border bg-surface text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800',
        abierto && !activo && 'bg-slate-100 dark:bg-slate-800',
      )}
    >
      <Icono aria-hidden className="h-4 w-4 shrink-0" />
      <span className={cn('max-w-[11rem] truncate', texto)}>{etiqueta}</span>
      {/* Solo con el icono, el punto va en la esquina; con el texto, a su lado. */}
      {activo && <span aria-hidden className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-brand-600 lg:static dark:bg-brand-300" />}
      {conFlecha && <ChevronDown aria-hidden className={cn('h-4 w-4 shrink-0 opacity-60 transition-transform motion-reduce:transition-none', texto, abierto && 'rotate-180')} />}
    </button>
  );
}

/** Una lista de «elige una»: botones de radio de verdad (las flechas del teclado funcionan solas). */
function Opciones<T extends string>({ grupo, opciones, valor, onElegir }: { grupo: 'orden' | 'agrupar' | 'densidad'; opciones: ReadonlyArray<{ id: T; etiqueta: string }>; valor: T; onElegir: (id: T) => void }) {
  return (
    <div role="radiogroup" aria-label={PANELES[grupo].titulo}>
      {opciones.map((o) => (
        <label
          key={o.id}
          className={cn(
            'flex min-h-[44px] cursor-pointer items-center gap-2.5 rounded-md px-2 text-sm transition-colors hover:bg-slate-100 md:min-h-[36px] dark:hover:bg-slate-800',
            o.id === valor ? 'font-medium text-brand-700 dark:text-brand-200' : 'text-slate-700 dark:text-slate-200',
          )}
        >
          <input type="radio" name={`clientes-${grupo}`} checked={o.id === valor} onChange={() => onElegir(o.id)} className="h-4 w-4 shrink-0 accent-brand-600" />
          {o.etiqueta}
        </label>
      ))}
    </div>
  );
}

const FLECHA = cn(
  'grid h-11 w-11 shrink-0 place-items-center rounded-md text-slate-500 transition-colors hover:bg-slate-200/70 hover:text-slate-800 disabled:opacity-30 disabled:hover:bg-transparent md:h-8 md:w-8 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-100',
  FOCO,
);
const ENLACE = cn('inline-flex min-h-[44px] items-center rounded text-sm font-medium text-brand-700 hover:underline disabled:text-slate-400 disabled:no-underline md:min-h-0 dark:text-brand-300 dark:disabled:text-slate-500', FOCO);

/**
 * El panel «Columnas» de la tabla: mostrar u ocultar cada una y subirla o bajarla de sitio.
 * `columnas` ya viene en orden (delante las que se ven); `guardadas` es `pref.columnas`. Sin estado.
 */
export function PanelDeColumnas({ columnas, guardadas, onColumnas }: { columnas: ReadonlyArray<{ id: string; titulo: string }>; guardadas: readonly string[]; onColumnas: (columnas: string[]) => void }) {
  const aLaVista = columnasALaVista(guardadas);
  const todas = columnas.map((c) => c.id);
  const vistas = columnas.filter((c) => aLaVista.includes(c.id));
  const ocultas = columnas.filter((c) => !aLaVista.includes(c.id));
  const deEntrada = aLaVista.join() === COLUMNAS_DE_ENTRADA.join();
  const fila = (c: { id: string; titulo: string }, visible: boolean, sitio: number) => (
    <li key={c.id} data-columna={c.id} className="flex items-center rounded-md hover:bg-slate-100 dark:hover:bg-slate-800">
      <label className={cn('flex min-h-[44px] min-w-0 flex-1 items-center gap-2.5 px-2 text-sm text-slate-700 md:min-h-[36px] dark:text-slate-200', c.id === 'cliente' ? 'cursor-not-allowed' : 'cursor-pointer')}>
        <input type="checkbox" checked={visible} disabled={c.id === 'cliente'} onChange={() => onColumnas(alternarColumna(guardadas, c.id))} className="h-4 w-4 shrink-0 accent-brand-600" />
        <span className="truncate">{c.titulo}</span>
        {c.id === 'cliente' && <span className="ml-auto shrink-0 pr-1 text-xs text-slate-500 dark:text-slate-400">siempre</span>}
      </label>
      {visible && c.id !== 'cliente' && (
        <>
          {/* «Cliente» es la primera y nadie le pasa por delante: la que le sigue ya no sube. */}
          <button type="button" aria-label={`Subir ${c.titulo}`} title="Subir de sitio" disabled={sitio <= 1} onClick={() => onColumnas(moverColumna(guardadas, c.id, -1, todas))} className={FLECHA}>
            <ArrowUp className="h-4 w-4" />
          </button>
          <button type="button" aria-label={`Bajar ${c.titulo}`} title="Bajar de sitio" disabled={sitio >= vistas.length - 1} onClick={() => onColumnas(moverColumna(guardadas, c.id, 1, todas))} className={FLECHA}>
            <ArrowDown className="h-4 w-4" />
          </button>
        </>
      )}
    </li>
  );
  return (
    <div className="space-y-2">
      <ul aria-label="Columnas a la vista">{vistas.map((c, i) => fila(c, true, i))}</ul>
      {ocultas.length > 0 && (
        <>
          <p className="border-t border-surface-border px-2 pt-2.5 text-xs font-medium text-slate-500 dark:text-slate-400">Ocultas</p>
          <ul aria-label="Columnas ocultas">{ocultas.map((c) => fila(c, false, -1))}</ul>
        </>
      )}
      <div className="border-t border-surface-border px-2 pt-2">
        <button type="button" disabled={deEntrada} onClick={() => onColumnas([])} className={ENLACE}>
          Volver a las de entrada
        </button>
      </div>
    </div>
  );
}

/**
 * Dónde va el panel en escritorio: debajo de su botón, alineado con él por la izquierda y sin pasar
 * del borde derecho de la barra (ni de la pantalla). En el teléfono es una hoja: la colocan las clases.
 */
function dondeVa(boton: Element, ancho: number): CSSProperties {
  if (!window.matchMedia('(min-width: 768px)').matches) return {};
  const r = boton.getBoundingClientRect();
  const tope = Math.min(boton.closest('section')?.getBoundingClientRect().right ?? Infinity, window.innerWidth - 8);
  const hueco = window.innerHeight - r.bottom - 16;
  return { left: Math.max(8, Math.min(r.left, tope - ancho)), top: r.bottom + 8, width: ancho, maxHeight: Math.max(200, hueco) };
}

export function BarraDeClientes({ pref, onPref, busqueda, onBuscar, etapas, etapa, cuentas, fuentes = [], columnas, onImportar, onExportar, resultados, panelDeEntrada = null }: BarraDeClientesProps) {
  const [abierto, setAbierto] = useState<{ panel: PanelDeBarra; boton: HTMLElement | null; estilo: CSSProperties } | null>(panelDeEntrada ? { panel: panelDeEntrada, boton: null, estilo: {} } : null);
  const caja = useRef<HTMLDivElement>(null);
  const panel = abierto?.panel ?? null;
  const boton = abierto?.boton ?? null;

  useEffect(() => {
    if (!panel) return;
    // El foco entra al panel: con Tab se sigue por lo que hay dentro, no por el resto de la barra.
    caja.current?.focus({ preventScroll: true });
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setAbierto(null);
      // Y vuelve al botón que lo abrió.
      boton?.focus();
    };
    // Fijo a la pantalla, se quedaría flotando lejos de su botón si la página se desplaza: lo sigue.
    const seguir = () => setAbierto((a) => (a?.boton ? { ...a, estilo: dondeVa(a.boton, PANELES[a.panel].ancho) } : a));
    document.addEventListener('keydown', alTeclear);
    window.addEventListener('scroll', seguir, true);
    window.addEventListener('resize', seguir);
    return () => {
      document.removeEventListener('keydown', alTeclear);
      window.removeEventListener('scroll', seguir, true);
      window.removeEventListener('resize', seguir);
    };
  }, [panel, boton]);

  const cerrar = () => setAbierto(null);
  const alternar = (cual: PanelDeBarra) => (e: React.MouseEvent<HTMLButtonElement>) => {
    const suBoton = e.currentTarget;
    setAbierto((a) => (a?.panel === cual ? null : { panel: cual, boton: suBoton, estilo: dondeVa(suBoton, PANELES[cual].ancho) }));
  };
  const poner = (cambio: Partial<PrefCartera>) => onPref((antes) => ({ ...antes, ...cambio }));

  const enTabla = pref.vista === 'tabla';
  const fabrica = PREF_CARTERA_DE_FABRICA;
  const facetas = facetasDeClientes(etapas, cuentas, fuentes);
  // Fuente: la preferencia guarda varias (vistas viejas); la barra elige una. Si hay varias, sale como chip aparte.
  // Una sola y entre las de la lista: va en la faceta. Varias, o una que la lista ya no trae: chip aparte, que se pueda quitar.
  const unaFuente = pref.fuentes.length === 1 && fuentes.some((x) => x.fuente === pref.fuentes[0]) ? pref.fuentes[0] : undefined;
  const valores = { quienes: pref.filtro === fabrica.filtro ? undefined : pref.filtro, etapa: etapa || undefined, fuente: unaFuente };
  const chipsDeFuentes = pref.fuentes.length > 0 && !unaFuente ? [{ clave: 'fuentes', etiqueta: `Fuente: ${pref.fuentes.join(', ')}`, onQuitar: () => poner({ fuentes: [] }) }] : [];
  // La salud de las fuentes mide a todos: buscar y filtrar no aplican ahí.
  const deLista = pref.vista !== 'fuentes';
  const puesta = vistaAplicada(pref);
  const nombreDeLaPuesta = puesta && pref.guardadas.find((g) => g.id === puesta.id)?.nombre;
  const columnasCambiadas = columnasALaVista(pref.columnas).join() !== COLUMNAS_DE_ENTRADA.join();

  let contenido: ReactNode = null;
  if (panel === 'vistas') contenido = <PanelDeVistas pref={pref} onPref={onPref} />;
  else if (panel === 'orden') contenido = <Opciones grupo="orden" opciones={ORDENES} valor={pref.orden} onElegir={(orden) => poner({ orden })} />;
  else if (panel === 'agrupar') contenido = <Opciones grupo="agrupar" opciones={AGRUPAR} valor={pref.agrupar} onElegir={(agrupar) => poner({ agrupar })} />;
  else if (panel === 'columnas') contenido = <PanelDeColumnas columnas={columnas} guardadas={pref.columnas} onColumnas={(nuevas) => poner({ columnas: nuevas })} />;
  else if (panel === 'densidad') contenido = <Opciones grupo="densidad" opciones={DENSIDADES} valor={pref.densidad} onElegir={(densidad) => poner({ densidad })} />;

  return (
    <section aria-label="Herramientas de la lista" className="space-y-2">
      {/* Renglón 1: cómo verlos y, a la derecha, lo que es de la pantalla entera. */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
        <SelectorDeVista opciones={VISTAS} valor={pref.vista} onCambiar={(vista) => poner({ vista })} etiqueta="Cómo ver a tus clientes" />
        <div className="ml-auto flex items-center gap-1.5">
          <BotonDeBarra
            icono={Bookmark}
            etiqueta={nombreDeLaPuesta || 'Mis vistas'}
            nombre={nombreDeLaPuesta ? `Mis vistas: ${nombreDeLaPuesta}${puesta?.modificada ? ' (modificada)' : ''}` : 'Mis vistas'}
            ayuda={nombreDeLaPuesta ? (puesta?.modificada ? `«${nombreDeLaPuesta}», con cambios sin guardar` : `Estás viendo «${nombreDeLaPuesta}»`) : 'Guarda la pantalla como te gusta y vuelve a ella con un toque'}
            abierto={panel === 'vistas'}
            activo={Boolean(puesta?.modificada)}
            conFlecha
            onClick={alternar('vistas')}
          />
          {onImportar && <BotonDeBarra icono={Upload} etiqueta="Importar contactos" conTextoDesde="xl" onClick={onImportar} />}
          {onExportar && <BotonDeBarra icono={Download} etiqueta="Exportar" conTextoDesde="xl" ayuda="Bajar a Excel los clientes que estás viendo, con las columnas de la tabla" onClick={onExportar} />}
        </div>
      </div>

      {/* Renglón 2: el superfiltro (buscar, a quiénes y la etapa, con sus chips) y, al final, cómo ordenarlos y verlos. */}
      {deLista && (
      <Superfiltro
        chipsExtra={chipsDeFuentes}
        buscar={busqueda}
        onBuscar={onBuscar}
        placeholder="Buscar por nombre, teléfono, email o país…"
        facetas={facetas}
        valores={valores}
        onCambiar={(c) =>
          poner({
            ...('quienes' in c ? { filtro: (c.quienes ?? fabrica.filtro) as Filtro } : {}),
            ...('etapa' in c ? { etapa: c.etapa ?? fabrica.etapa } : {}),
            ...('fuente' in c ? { fuentes: c.fuente ? [c.fuente] : [] } : {}),
          })
        }
        onLimpiar={() => {
          // Dos almacenes distintos (la preferencia y la URL): no se pisan.
          poner({ filtro: fabrica.filtro, etapa: fabrica.etapa, fuentes: fabrica.fuentes });
          onBuscar('');
        }}
        resultados={resultados}
        acciones={
          <>
            <BotonDeBarra icono={ArrowDownUp} etiqueta="Ordenar" abierto={panel === 'orden'} activo={pref.orden !== fabrica.orden} onClick={alternar('orden')} />
            {enTabla && (
              <>
                <BotonDeBarra icono={Group} etiqueta="Agrupar" abierto={panel === 'agrupar'} activo={pref.agrupar !== fabrica.agrupar} onClick={alternar('agrupar')} />
                <BotonDeBarra icono={Columns3} etiqueta="Columnas" abierto={panel === 'columnas'} activo={columnasCambiadas} onClick={alternar('columnas')} />
                <BotonDeBarra icono={Rows3} etiqueta="Densidad" abierto={panel === 'densidad'} activo={pref.densidad !== fabrica.densidad} onClick={alternar('densidad')} />
              </>
            )}
          </>
        }
      />
      )}

      {panel && (
        <>
          {/* Tocar fuera cierra. En el teléfono, además, oscurece lo de atrás. */}
          <button type="button" aria-label="Cerrar" tabIndex={-1} onClick={cerrar} className="fixed inset-0 z-40 !mt-0 cursor-default bg-slate-900/40 motion-safe:animate-fade-in md:bg-transparent" />
          <div
            ref={caja}
            role="dialog"
            aria-label={PANELES[panel].titulo}
            tabIndex={-1}
            style={abierto?.estilo}
            className={cn(
              // `!mt-0`: fuera del flujo, el aire entre renglones de la barra (`space-y-2`) no es suyo.
              'fixed inset-x-0 bottom-0 z-40 !mt-0 flex max-h-[85dvh] flex-col rounded-t-2xl border border-surface-border bg-surface shadow-2xl outline-none motion-safe:animate-subir',
              'md:inset-x-auto md:bottom-auto md:rounded-xl md:shadow-lg md:motion-safe:animate-scale-in',
            )}
          >
            <div className="flex shrink-0 items-center justify-between gap-3 pb-1 pl-4 pr-2 pt-2 md:px-3 md:pb-0 md:pt-3">
              <h2 className="text-base font-semibold text-slate-900 md:text-xs md:font-medium md:text-slate-500 dark:text-slate-100 md:dark:text-slate-400">{PANELES[panel].titulo}</h2>
              <button type="button" onClick={cerrar} className={cn('min-h-[44px] rounded-md px-3 text-sm font-semibold text-brand-700 md:hidden dark:text-brand-300', FOCO)}>
                Listo
              </button>
            </div>
            <div className="min-h-0 overflow-y-auto overscroll-contain px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-1 md:px-2 md:pb-2 md:pt-1.5">{contenido}</div>
          </div>
        </>
      )}
    </section>
  );
}
